/**
 * Who can use this, for admins.
 *
 * Accounts are disabled rather than deleted: every row in the audit trail names an email, and
 * removing the account would turn that history into a puzzle. Disabling ends their sessions at
 * once, so it is the immediate action when someone leaves.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, type Role, type User } from '../api';

function ago(timestamp: number | null): string {
  if (!timestamp) return 'never';
  const days = Math.floor((Date.now() - timestamp) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(timestamp).toLocaleDateString();
}

export function People({ me, onToast }: { me: User; onToast: (message: string) => void }) {
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const reload = useCallback(async () => {
    try {
      setUsers(await api.users());
      setError(null);
    } catch (failure) {
      setError((failure as ApiError).message);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  const change = async (user: User, patch: Parameters<typeof api.updateUser>[1], done: string) => {
    try {
      await api.updateUser(user.id, patch);
      onToast(done);
      void reload();
    } catch (failure) {
      onToast((failure as ApiError).message);
    }
  };

  return (
    <div className="inner">
      <header className="run-head">
        <div className="run-title"><h1>People</h1></div>
        <div className="actions">
          <button type="button" className="btn" onClick={() => setAdding((open) => !open)}>
            {adding ? 'Cancel' : 'Add someone'}
          </button>
        </div>
      </header>

      {error && <div className="errors section"><span>{error}</span></div>}

      {adding && (
        <AddPerson
          onAdded={(email) => { setAdding(false); onToast(`${email} can now sign in`); void reload(); }}
          onError={onToast}
        />
      )}

      {users.length === 0 && !error ? (
        <div className="empty">
          <h1>Only you so far</h1>
          <p>Add the developers who create clients and run builds.</p>
        </div>
      ) : (
      <section className="section">
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Last signed in</th>
                <th>Added by</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className={user.disabled ? 'person-off' : undefined}>
                  <td>
                    {user.email}
                    {user.id === me.id && <span className="tag you">you</span>}
                    {user.mustChangePassword && !user.disabled && (
                      <span className="tag pending">password not yet chosen</span>
                    )}
                  </td>
                  <td>
                    <select
                      id={`role-${user.id}`}
                      value={user.role}
                      disabled={user.disabled}
                      style={{ width: 118 }}
                      onChange={(event) => void change(user, { role: event.target.value as Role },
                        `${user.email} is now ${event.target.value === 'admin' ? 'an admin' : 'a member'}`)}
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td style={{ color: 'var(--ink-2)' }}>{user.disabled ? 'disabled' : ago(user.lastLoginAt)}</td>
                  <td style={{ color: 'var(--ink-3)' }}>{user.createdBy || '—'}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <ResetPassword user={user} onToast={onToast} onDone={reload} />
                    <button
                      type="button"
                      className="btn quiet small"
                      style={{ marginLeft: 6 }}
                      onClick={() => void change(user, { disabled: !user.disabled },
                        user.disabled ? `${user.email} can sign in again` : `${user.email} is disabled`)}
                    >
                      {user.disabled ? 'Enable' : 'Disable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="hint" style={{ marginTop: 10 }}>
          Disabling ends their sessions at once. Accounts are never deleted.
        </p>
      </section>
      )}
    </div>
  );
}

function ResetPassword({ user, onToast, onDone }: {
  user: User;
  onToast: (message: string) => void;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');

  if (!open) {
    return (
      <button type="button" className="btn quiet small" onClick={() => setOpen(true)} disabled={user.disabled}>
        Set password
      </button>
    );
  }

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <input
        id={`pw-${user.id}`}
        type="text"
        autoFocus
        placeholder="new password"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        style={{ width: 180 }}
      />
      <button
        type="button"
        className="btn small"
        onClick={() => {
          api.updateUser(user.id, { password: value })
            .then(() => {
              onToast(`${user.email} must choose their own next time they sign in`);
              setOpen(false);
              setValue('');
              onDone();
            })
            .catch((failure: ApiError) => onToast(failure.message));
        }}
      >
        Set
      </button>
      <button type="button" className="btn quiet small" onClick={() => { setOpen(false); setValue(''); }}>
        Cancel
      </button>
    </span>
  );
}

function AddPerson({ onAdded, onError }: { onAdded: (email: string) => void; onError: (message: string) => void }) {
  const [busy, setBusy] = useState(false);

  return (
    <form
      className="form section"
      onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        setBusy(true);
        api.createUser({
          email: String(form.get('email')),
          name: String(form.get('name') ?? ''),
          password: String(form.get('password')),
          role: String(form.get('role')) as Role,
        })
          .then((user) => onAdded(user.email))
          .catch((failure: ApiError) => onError(failure.message))
          .finally(() => setBusy(false));
      }}
    >
      <div className="pair">
        <div className="field">
          <label htmlFor="new-email">Email</label>
          <input id="new-email" name="email" type="email" required placeholder="dev@3ds.gr" />
        </div>
        <div className="field">
          <label htmlFor="new-name">Name (optional)</label>
          <input id="new-name" name="name" type="text" />
        </div>
      </div>
      <div className="pair">
        <div className="field">
          <label htmlFor="new-password">First password</label>
          <input id="new-password" name="password" type="text" required minLength={12}
                 placeholder="at least 12 characters" />
          <span className="hint">They replace it on first sign-in.</span>
        </div>
        <div className="field">
          <label htmlFor="new-role">Role</label>
          <select id="new-role" name="role" defaultValue="member">
            <option value="member">Member — clients and builds</option>
            <option value="admin">Admin — also manages people</option>
          </select>
        </div>
      </div>
      <div className="actions">
        <button className="btn" type="submit" disabled={busy}>{busy ? 'Adding' : 'Add'}</button>
      </div>
    </form>
  );
}
